import { modifyMediaUrl } from "../helpers/modifyMediaUrl";
import { useState, useEffect, useRef } from "react";
import { getFeaturedTeachersData } from "../services/homePageService";
import { HOME_PAGE_CONSTANTS } from "../constants/app-constants";
import { i18n, initializeI18n } from "../localization/i18n";
import { translate } from "../localization/translateService";
import TrainerDetailModal from "./trainer/TrainerDetailModal";
import ErrorBoundary from "./common/ErrorBoundary";
import LanguageFlag from "./trainer/components/common/LanguageFlag";
import { getCountryCodeFromLanguage } from "../helpers/countryHelper";
import { LANGUAGES } from "../constants/language";
import { CarouselMotion } from "../helpers/carouselMotion";

const byRating = (a, b) =>
  (b?.rating ?? 0) - (a?.rating ?? 0) || (b?.numberRating ?? 0) - (a?.numberRating ?? 0);

// "INTERMEDIATE" / "UPPER_INTERMEDIATE" -> "Intermediate" / "Upper Intermediate"
const prettyLevel = (code) =>
  !code ? null : String(code).toLowerCase().replace(/_/g, " ").replace(/(^|\s)\w/g, (c) => c.toUpperCase());

const deriveLevel = (item) => {
  const lang = (item?.languages || [])[0];
  if (!lang) return null;
  const prof = lang.proficiencyDescription || lang.proficiency;
  // proficiencyDescription is often a raw i18n key (e.g. COMMON_ITEMS.LANGUAGE_PROFICIENCY.INTERMEDIATE);
  // in that case fall back to the proficiencyCode rendered nicely.
  if (!prof || /[A-Z_]{3,}\.[A-Z_]/.test(prof)) return prettyLevel(lang.proficiencyCode);
  return prof;
};

const bgStyle = (url) =>
  url ? { backgroundImage: `url(${modifyMediaUrl(url)})` } : undefined;

const LAYOUT = {
  INFO_W: 330,
  PHOTO_SIDE: 254,
  PHOTO_GROW: 45,
  H_SIDE: 254,
  H_GROW: 138,
  GAP: 24,
};

export default function FeaturedTeacherSection({ props }) {
  const [list, setList] = useState([]);
  const [active, setActive] = useState(0);
  const [lang, setLang] = useState(null);
  const [t, setT] = useState({});
  const [openSeq, setOpenSeq] = useState(0); // bumped on each card click -> resets modal ErrorBoundary
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 720
  );

  const modalRef = useRef(null);
  const viewportRef = useRef(null);
  const viewportMobileRef = useRef(null);
  const elsRef = useRef({});     // index -> { root, info, photo } DOM nodes cho engine
  const elsMobileRef = useRef({}); // index -> mobile card root DOM node
  const engineRef = useRef(null);
  const listRef = useRef([]);
  const activateRef = useRef(() => {});
  const tapRef = useRef(() => {});
  listRef.current = list;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 720);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!i18n.isInitialized) initializeI18n().then(() => setLang(i18n.language));
    else setLang(i18n.language);
  }, []);

  // i18n defaults — used only when the CMS field is empty (no hardcoded English in markup)
  useEffect(() => {
    let alive = true;
    (async () => {
      const out = {};
      out.title = await translate("UMBRACO_LANDING.HOME.TUTOR.TITLE", false, "Popular Rising Instructor");
      out.subtitle = await translate("UMBRACO_LANDING.HOME.TUTOR.SUBTITLE", false, "Choose verified tutors based on your level, learning goal, schedule, and preferred learning style.");
      out.ctaLabel = await translate("UMBRACO_LANDING.HOME.TUTOR.CTA_LABEL", false, "Find Your Tutor");
      out.learners = await translate("UMBRACO_LANDING.HOME.TUTOR.LEARNERS", false, "learners");
      out.yearsExperience = await translate("UMBRACO_LANDING.HOME.TUTOR.YEARS_EXPERIENCE", false, "Years Experience");
      if (alive) setT(out);
    })();
    return () => { alive = false; };
  }, [lang]);

  useEffect(() => {
    (async () => {
      const d = await getFeaturedTeachersData({ quantity: HOME_PAGE_CONSTANTS.GET_FEATURED_TEACHER_QUANTITY });
      const arr = Array.isArray(d) ? [...d].sort(byRating) : [];
      setList(arr);
      setActive(arr.length ? Math.min(2, arr.length - 1) : 0);
    })();
  }, []);

  // Preload every tutor image once so morphing never waits on a decode.
  useEffect(() => {
    list.forEach((it) => {
      const u = it?.imageUrl || props?.featuredTeacherSectionDefaultTeacherImage;
      if (u) { const im = new Image(); im.src = modifyMediaUrl(u); }
    });
  }, [list]);

  useEffect(() => {
    const N = list.length;
    if (N < 1 || !viewportRef.current) return;
    const reduceMotion = typeof window !== "undefined" && window.matchMedia
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const engine = new CarouselMotion({
      count: N,
      autoDelayMs: 4000,
      firstDelayMs: 1500,
      autoplay: !reduceMotion,
      layout: LAYOUT,
      getEl: (i) => elsRef.current[i],
      getElMobile: (i) => elsMobileRef.current[i],
      onActiveChange: (i) => setActive(i),
      onTap: (x, y) => tapRef.current(x, y),
    });

    const startIdx = Math.min(2, N - 1);
    engine.p = startIdx; engine.target = startIdx; engine.active = startIdx;
    engineRef.current = engine;
    engine.setMobile(typeof window !== "undefined" && window.innerWidth < 720);
    engine.start();
    let armObserver = null;
    const armTargets = [viewportRef.current, viewportMobileRef.current].filter(Boolean);
    if (typeof IntersectionObserver !== "undefined" && armTargets.length) {
      armObserver = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          engine.armAutoplay();
          if (armObserver) { armObserver.disconnect(); armObserver = null; }
        }
      }, { threshold: 0.35 });
      armTargets.forEach((el) => armObserver.observe(el));
    } else {
      engine.armAutoplay();
    }

    return () => { if (armObserver) armObserver.disconnect(); engine.destroy(); engineRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.length]);

  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;
    engine.setMobile(isMobile);
    if (list.length <= 1) return;

    const target = isMobile ? viewportMobileRef.current : viewportRef.current;
    if (!target || engine.viewport === target) return;

    const old = engine.viewport;
    if (old) {
      old.removeEventListener("pointerdown", engine._down);
      old.removeEventListener("pointermove", engine._move);
      old.removeEventListener("pointerup", engine._up);
      old.removeEventListener("pointercancel", engine._up);
      old.removeEventListener("mouseenter", engine._enter);
      old.removeEventListener("mouseleave", engine._leave);
    }
    engine.bind(target);
  }, [isMobile, list.length]);

  const title = props?.featuredTeacherSectionTitle1 || t.title || "";
  const subtitle = props?.featuredTeacherSectionTitle2 || t.subtitle || "";
  const ctaLabel = props?.featuredTeacherCtaLabel || t.ctaLabel || "";
  const ctaLink = props?.featuredTeacherCtaLink || `/${lang || "en"}/real-time-lectures?tab=allTutors&page=1&rows=5`;
  const defaultAvatar = props?.featuredTeacherSectionDefaultTeacherImage;

  const openTutor = (f) => {
    if (f?.publicId) {
      const seg = window.location.pathname.split("/")[1];
      const curLang = LANGUAGES.includes(seg) ? seg : (lang || "en");
      window.location.href = `/${curLang}/real-time-lectures/${f.publicId}`;
      return;
    }
    if (f?.id != null && modalRef.current) { setOpenSeq((n) => n + 1); modalRef.current.open(f.id); }
  };

  activateRef.current = (i) => {
    const eng = engineRef.current;
    if (eng && Math.abs(eng.offsetOf(i)) > 0.4) { eng.goTo(i); return; }
    openTutor(listRef.current[i]);
  };

  tapRef.current = (x, y) => {
    const el = typeof document !== "undefined" ? document.elementFromPoint(x, y) : null;
    const card = el && el.closest ? el.closest(".tutor-cf, .tutor-cf-m") : null;
    if (!card) return;
    const idx = Number(card.getAttribute("data-idx"));
    if (!Number.isNaN(idx)) activateRef.current(idx);
  };

  const onDot = (i) => { const eng = engineRef.current; if (eng) eng.goTo(i); else setActive(i); };

  const setRef = (i, part) => (el) => {
    const cur = elsRef.current[i] || (elsRef.current[i] = {});
    cur[part] = el;
  };

  const setRefMobile = (i) => (el) => {
    elsMobileRef.current[i] = el;
  };

  return (
    <section className="tutor-section">
      <div className="tutor-section__head">
        <h2 className="tutor-section__title animate-on-scroll">{title}</h2>
        <p className="tutor-section__subtitle animate-on-scroll">{subtitle}</p>
      </div>

      {list.length > 0 && (
        <div className="tutor-viewport animate-on-scroll" ref={viewportRef}>
          {list.map((f, i) => {
            const fImg = f?.imageUrl || defaultAvatar;
            const lrn = f?.learners ?? f?.learnerCount ?? f?.numberOfStudents ?? f?.totalStudents ?? f?.totalLearners;
            const yRaw = f?.yearsOfExperience ?? f?.experienceYears ?? f?.yearsExperience;
            const yrs = (typeof yRaw === "number" || /^\d+$/.test(String(yRaw ?? ""))) ? yRaw : null;
            const lvl = deriveLevel(f);
            const flag = getCountryCodeFromLanguage(f?.languages?.[0]?.languageCode);
            return (
              <div key={f?.id ?? i} className="tutor-cf" data-idx={i} ref={setRef(i, "root")}
                   role="button" tabIndex={0}
                   onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); activateRef.current(i); } }}>
                <div className="tutor-cf__info" ref={setRef(i, "info")}>
                  <div className="tutor-cf__info-inner">
                    <div className="tutor-vid" style={bgStyle(fImg)}>
                      <span className="tutor-vid__play" aria-hidden="true">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                      </span>
                    </div>
                    <div className="tutor-info">
                      <div className="tutor-info__nm">
                        <span className="tutor-info__flag" aria-hidden="true">
                          {flag && <LanguageFlag code={flag} className="tutor-info__flag-img" />}
                        </span>
                        <div>
                          <b>{f?.name}</b>
                          {lrn != null && lrn !== "" && <span>{lrn} {t.learners}</span>}
                        </div>
                      </div>
                      {yrs != null && yrs !== "" && Number(yrs) > 0 && (
                        <div className="tutor-info__row">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12.1504 16.5V18.6" /><path d="M7.15039 22H17.1504V21C17.1504 19.9 16.2504 19 15.1504 19H9.15039C8.05039 19 7.15039 19.9 7.15039 21V22Z" /><path d="M6.15039 22H18.1504" /><path d="M12 16C8.13 16 5 12.87 5 9V6C5 3.79 6.79 2 9 2H15C17.21 2 19 3.79 19 6V9C19 12.87 15.87 16 12 16Z" /><path d="M5.46906 11.6501C4.71906 11.4101 4.05906 10.9701 3.53906 10.4501C2.63906 9.4501 2.03906 8.2501 2.03906 6.8501C2.03906 5.4501 3.13906 4.3501 4.53906 4.3501H5.18906C4.98906 4.8101 4.88906 5.3201 4.88906 5.8501V8.8501C4.88906 9.8501 5.09906 10.7901 5.46906 11.6501Z" /><path d="M18.5293 11.6501C19.2793 11.4101 19.9393 10.9701 20.4593 10.4501C21.3593 9.4501 21.9593 8.2501 21.9593 6.8501C21.9593 5.4501 20.8593 4.3501 19.4593 4.3501H18.8093C19.0093 4.8101 19.1093 5.3201 19.1093 5.8501V8.8501C19.1093 9.8501 18.8993 10.7901 18.5293 11.6501Z" /></svg>
                          {yrs} {t.yearsExperience}
                        </div>
                      )}
                      {lvl && (
                        <div className="tutor-info__row">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M8.67 14H4C2.9 14 2 14.9 2 16V22H8.67V14Z" /><path d="M13.3302 10H10.6602C9.56016 10 8.66016 10.9 8.66016 12V22H15.3302V12C15.3302 10.9 14.4402 10 13.3302 10Z" /><path d="M20.0001 17H15.3301V22H22.0001V19C22.0001 17.9 21.1001 17 20.0001 17Z" /><path d="M12.5202 2.06982L13.0502 3.12982C13.1202 3.27982 13.3102 3.41982 13.4702 3.43982L14.4302 3.59982C15.0402 3.69982 15.1902 4.1498 14.7502 4.5798L14.0002 5.3298C13.8702 5.4598 13.8002 5.69981 13.8402 5.86981L14.0502 6.78982C14.2202 7.51982 13.8302 7.79983 13.1902 7.41983L12.2902 6.88983C12.1302 6.78983 11.8602 6.78983 11.7002 6.88983L10.8002 7.41983C10.1602 7.79983 9.77023 7.51982 9.94023 6.78982L10.1502 5.86981C10.1902 5.69981 10.1202 5.4498 9.99023 5.3298L9.25023 4.58981C8.81023 4.14981 8.95023 3.7098 9.57023 3.6098L10.5302 3.44983C10.6902 3.41983 10.8802 3.27983 10.9502 3.13983L11.4802 2.0798C11.7702 1.4998 12.2302 1.49982 12.5202 2.06982Z" /></svg>
                          {lvl}
                        </div>
                      )}
                      {f?.rating != null && (
                        <div className="tutor-info__row">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="var(--dotore-yellow)" stroke="none"><path d="M11.9969 2C12.9691 2.00017 13.7381 2.72927 14.2303 3.71777L15.868 6.99121L15.8699 6.99707C15.919 7.0971 16.0359 7.2396 16.2127 7.37012C16.3894 7.50049 16.5635 7.57258 16.6785 7.5918L19.6453 8.08496C20.7138 8.26324 21.6142 8.78346 21.907 9.69434C22.1999 10.6061 21.7693 11.5503 20.9998 12.3154L21.0008 12.3164L18.6941 14.623C18.6034 14.7139 18.502 14.8849 18.4383 15.1074C18.3754 15.3271 18.3696 15.5264 18.3973 15.6553L19.0574 18.5107L19.1043 18.7334C19.317 19.845 19.1875 20.9197 18.3943 21.498C17.5495 22.114 16.3958 21.8426 15.3445 21.2217L12.5623 19.5742C12.445 19.505 12.244 19.4493 12.0017 19.4492C11.7595 19.4492 11.5522 19.5047 11.4275 19.5771L8.65213 21.2217L8.65116 21.2207C7.6013 21.8435 6.44905 22.1105 5.60428 21.4941C4.75909 20.8771 4.66342 19.6975 4.93827 18.5098L5.59842 15.6553C5.62605 15.5264 5.62026 15.327 5.55741 15.1074C5.49369 14.8849 5.39223 14.7139 5.30155 14.623L2.99491 12.3164C2.22964 11.5511 1.80077 10.6063 2.09159 9.69531C2.3827 8.78374 3.28095 8.26328 4.35038 8.08496L7.31424 7.5918C7.42386 7.57318 7.59638 7.50219 7.77323 7.37109C7.94972 7.24018 8.06673 7.0973 8.116 6.99707L9.75565 3.71777L9.75663 3.7168C10.2534 2.72838 11.0252 2.00003 11.9969 2Z" /></svg>
                          {Number(f.rating).toFixed(1)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="tutor-cf__photo" ref={setRef(i, "photo")} style={bgStyle(fImg)} />
              </div>
            );
          })}
        </div>
      )}

      {list.length > 0 && (
        <div className="tutor-viewport-m animate-on-scroll" ref={viewportMobileRef}>
          {list.map((f, i) => {
            const fImg = f?.imageUrl || defaultAvatar;
            const lvl = deriveLevel(f);
            const flag = getCountryCodeFromLanguage(f?.languages?.[0]?.languageCode);
            return (
              <div key={f?.id ?? i} className="tutor-cf-m" data-idx={i} ref={setRefMobile(i)}
                   role="button" tabIndex={0}
                   onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); activateRef.current(i); } }}>
                <div className="tutor-cf-m__photo" style={bgStyle(fImg)} />
                <div className="tutor-cf-m__vid" style={bgStyle(fImg)}>
                  <span className="tutor-cf-m__play" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                  </span>
                </div>
                <div className="tutor-cf-m__info">
                  <div className="tutor-cf-m__nm">
                    <span className="tutor-cf-m__flag" aria-hidden="true">
                      {flag && <LanguageFlag code={flag} className="tutor-cf-m__flag-img" />}
                    </span>
                    <b>{f?.name}</b>
                  </div>
                  {lvl && (
                    <div className="tutor-cf-m__row tutor-cf-m__row--level">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M8.67 14H4C2.9 14 2 14.9 2 16V22H8.67V14Z" /><path d="M13.3302 10H10.6602C9.56016 10 8.66016 10.9 8.66016 12V22H15.3302V12C15.3302 10.9 14.4402 10 13.3302 10Z" /><path d="M20.0001 17H15.3301V22H22.0001V19C22.0001 17.9 21.1001 17 20.0001 17Z" /><path d="M12.5202 2.06982L13.0502 3.12982C13.1202 3.27982 13.3102 3.41982 13.4702 3.43982L14.4302 3.59982C15.0402 3.69982 15.1902 4.1498 14.7502 4.5798L14.0002 5.3298C13.8702 5.4598 13.8002 5.69981 13.8402 5.86981L14.0502 6.78982C14.2202 7.51982 13.8302 7.79983 13.1902 7.41983L12.2902 6.88983C12.1302 6.78983 11.8602 6.78983 11.7002 6.88983L10.8002 7.41983C10.1602 7.79983 9.77023 7.51982 9.94023 6.78982L10.1502 5.86981C10.1902 5.69981 10.1202 5.4498 9.99023 5.3298L9.25023 4.58981C8.81023 4.14981 8.95023 3.7098 9.57023 3.6098L10.5302 3.44983C10.6902 3.41983 10.8802 3.27983 10.9502 3.13983L11.4802 2.0798C11.7702 1.4998 12.2302 1.49982 12.5202 2.06982Z" /></svg>
                      <span>{lvl}</span>
                    </div>
                  )}
                  {f?.rating != null && (
                    <div className="tutor-cf-m__row tutor-cf-m__row--rating">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="var(--dotore-yellow)" stroke="none"><path d="M11.9969 2C12.9691 2.00017 13.7381 2.72927 14.2303 3.71777L15.868 6.99121L15.8699 6.99707C15.919 7.0971 16.0359 7.2396 16.2127 7.37012C16.3894 7.50049 16.5635 7.57258 16.6785 7.5918L19.6453 8.08496C20.7138 8.26324 21.6142 8.78346 21.907 9.69434C22.1999 10.6061 21.7693 11.5503 20.9998 12.3154L21.0008 12.3164L18.6941 14.623C18.6034 14.7139 18.502 14.8849 18.4383 15.1074C18.3754 15.3271 18.3696 15.5264 18.3973 15.6553L19.0574 18.5107L19.1043 18.7334C19.317 19.845 19.1875 20.9197 18.3943 21.498C17.5495 22.114 16.3958 21.8426 15.3445 21.2217L12.5623 19.5742C12.445 19.505 12.244 19.4493 12.0017 19.4492C11.7595 19.4492 11.5522 19.5047 11.4275 19.5771L8.65213 21.2217L8.65116 21.2207C7.6013 21.8435 6.44905 22.1105 5.60428 21.4941C4.75909 20.8771 4.66342 19.6975 4.93827 18.5098L5.59842 15.6553C5.62605 15.5264 5.62026 15.327 5.55741 15.1074C5.49369 14.8849 5.39223 14.7139 5.30155 14.623L2.99491 12.3164C2.22964 11.5511 1.80077 10.6063 2.09159 9.69531C2.3827 8.78374 3.28095 8.26328 4.35038 8.08496L7.31424 7.5918C7.42386 7.57318 7.59638 7.50219 7.77323 7.37109C7.94972 7.24018 8.06673 7.0973 8.116 6.99707L9.75565 3.71777L9.75663 3.7168C10.2534 2.72838 11.0252 2.00003 11.9969 2Z" /></svg>
                      <span>{Number(f.rating).toFixed(1)}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {list.length > 1 && (
        <div className="tutor-dots" role="tablist" aria-label="Instructors">
          {list.map((_, i) => (
            <button key={i} type="button" className={"tutor-dot" + (i === active ? " is-active" : "")}
              aria-label={`Instructor ${i + 1}`} onClick={() => onDot(i)} />
          ))}
        </div>
      )}

      <div className="tutor-section__cta">
        <a className="tutor-section__btn" href={ctaLink}>{ctaLabel}</a>
      </div>

      {/* Isolate the modal: a crash inside it must never unmount this whole section. */}
      <ErrorBoundary resetKey={openSeq}>
        <TrainerDetailModal ref={modalRef} defaultAvatar={defaultAvatar} />
      </ErrorBoundary>
    </section>
  );
}
